/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { mercuryBaseQuery } from 'api/base'
import { DNSModeType } from 'models/enums'
import { ContactsResponse, DNS, Domain, DomainRenewal, EligibilityDetail } from 'models/domain'
import { Invoice } from 'models/billing'

/**********************************************************************************************************
 *   ACCOUNT API
 **********************************************************************************************************/
export const domainAPI = createApi({
    reducerPath: 'domainAPI',
    baseQuery: mercuryBaseQuery,
    tagTypes: ['Domains', 'Domain', 'Records', 'Domain-Renewal', 'Domain-Contacts'],
    endpoints: (builder) => ({
        domainList: builder.query({
            query: ({ page = 1, filter_by, sort_by, search_by }) => `client/domains?page=${page}${sort_by ? `&sort_by=${sort_by}` : ''}${filter_by ? `&filter_by=${filter_by.key},${filter_by.value}` : ''}${search_by ? `&search_by=${search_by.key},~,${search_by.value}` : ''}`,
            providesTags: ['Domains']
        }),
        domain: builder.query<Domain, number>({
            query: (id) => `client/domains/${id}`,
            transformResponse: (response: { data: Domain }) => response.data,
            providesTags: ['Domain']
        }),
        eppCode: builder.query<string, number>({
            query: (id) => `client/domains/${id}/epp-code`,
            transformResponse: (response: { data: { password: string } }) => {
                return response.data.password
            }
        }),
        updateEPPCode: builder.mutation<void, { id: number, epp: string }>({
            query: ({ id, ...rest }) => ({
                url: `client/domains/${id}/epp-code`,
                method: 'PUT',
                body: rest
            })
        }),
        toggleAutoRenew: builder.mutation<Domain, { id: number, domain: Domain }>({
            query: ({ id }) => ({
                url: `client/domains/${id}/auto-renew`,
                method: 'POST'
            }),
            async onQueryStarted({ id, domain }, { dispatch, queryFulfilled }) {
                const domainResult = dispatch(
                    domainAPI.util.updateQueryData('domain', id, (draft) => {
                        Object.assign(draft, domain)
                    })
                )
                try {
                    await queryFulfilled
                } catch (error) {
                    domainResult.undo()
                }
            },
            invalidatesTags: ['Domain']
        }),
        toggleIdProtection: builder.mutation<Domain, { id: number, domain: Domain }>({
            query: ({ id }) => ({
                url: `client/domains/${id}/id-protection`,
                method: 'POST'
            }),
            async onQueryStarted({ id, domain }, { dispatch, queryFulfilled }) {
                const domainResult = dispatch(
                    domainAPI.util.updateQueryData('domain', id, (draft) => {
                        Object.assign(draft, domain)
                    })
                )
                try {
                    await queryFulfilled
                } catch (error) {
                    domainResult.undo()
                }
            },
            invalidatesTags: ['Domain']
        }),
        toggleTransferLock: builder.mutation<Domain, { id: number, domain: Domain }>({
            query: ({ id }) => ({
                url: `client/domains/${id}/transfer-lock`,
                method: 'POST'
            }),
            async onQueryStarted({ id, domain }, { dispatch, queryFulfilled }) {
                const domainResult = dispatch(
                    domainAPI.util.updateQueryData('domain', id, (draft) => {
                        Object.assign(draft, domain)
                    })
                )
                try {
                    await queryFulfilled
                } catch (error) {
                    domainResult.undo()
                }
            },
            invalidatesTags: ['Domain']
        }),
        eligibilityDetails: builder.query<EligibilityDetail[], number>({
            query: (id) => `client/domains/${id}/eligibility-details`,
            transformResponse: (response: { data: EligibilityDetail[] }) => response.data
        }),
        dnsRecords: builder.query<DNS, number>({
            query: (id) => `client/domains/${id}/dns/records`,
            transformResponse: (response: { data: DNS }) => response.data,
            providesTags: ['Records']
        }),
        addDNSRecords: builder.mutation({
            query: ({ id, ...rest }) => ({
                url: `client/domains/${id}/dns/records`,
                method: 'POST',
                body: rest
            }),
            invalidatesTags: ['Records']
        }),
        updateNameservers: builder.mutation<{ dns_config: string, nameservers: string[] }, { id: number, dns_config: DNSModeType | string, nameservers?: string[] }>({
            query: ({ id, ...rest }) => ({
                url: `client/domains/${id}/nameservers`,
                method: 'POST',
                body: rest
            }),
            transformResponse: (response: { data: { dns_config: string, nameservers: string[] } }) => response.data,
            invalidatesTags: ['Records']
        }),
        domainRenewal: builder.query<DomainRenewal, number>({
            query: (id) => `client/domains/${id}/renewal`,
            transformResponse: (response: { data: DomainRenewal }) => response.data,
            providesTags: ['Domain-Renewal']
        }),
        renewDomain: builder.mutation<Invoice, { id: number, billing_cycle_id: number, promo_code?: string }>({
            query: ({ id, ...rest }) => ({
                url: `client/domains/${id}/renewal`,
                method: 'POST',
                body: rest
            }),
            transformResponse: (response: { data: Invoice }) => response.data,
            invalidatesTags: ['Domain-Renewal']
        }),
        domainContacts: builder.query<ContactsResponse, number>({
            query: (id) => `client/domains/${id}/contacts`,
            transformResponse: (response: { data: ContactsResponse }) => response.data,
            providesTags: ['Domain-Contacts']
        }),
        updateDomainContacts: builder.mutation({
            query: ({ id, contacts }) => ({
                url: `client/domains/${id}/contacts`,
                method: 'PUT',
                body: { contacts }
            }),
            invalidatesTags: ['Domain-Contacts']
        })
    })
})

export const {
    useDomainListQuery,
    useDomainQuery,
    useDnsRecordsQuery,
    useEppCodeQuery,
    useUpdateEPPCodeMutation,
    useToggleAutoRenewMutation,
    useToggleIdProtectionMutation,
    useToggleTransferLockMutation,
    useEligibilityDetailsQuery,
    useAddDNSRecordsMutation,
    useUpdateNameserversMutation,
    useDomainRenewalQuery,
    useRenewDomainMutation,
    useDomainContactsQuery,
    useUpdateDomainContactsMutation
} = domainAPI
